import React, { useState, useMemo } from 'react';
import styles from './PositionCard.module.css';
import { formatSymbol } from '../utils/symbolFormatter';
import { isMarketOpen } from '../utils/marketTimeChecker';
import { FiChevronDown, FiPlus, FiXCircle, FiClock, FiRefreshCw, FiEdit2 } from 'react-icons/fi';
import { usePrice } from '../context/PriceContext';

const PositionCard = ({ order, onAction }) => {
  const {
    _id, tradeSymbol, tradeType, quantity, entryPrice,
    orderStatus, openPrice, finalPnL, targetPrice,
    stoplossPrice, margin, closePrice, exchange, positionType
  } = order;

  const { price: livePrice } = usePrice(tradeSymbol);
  const [showActions, setShowActions] = useState(false);

  const calculatePnl = () => {
    // margin is the leverage (e.g. 5)
    const leverage = margin || 1;
    if (orderStatus === 'Open' && livePrice) {
      const pnl = (livePrice - openPrice) * quantity * (tradeType.toLowerCase() === 'buy' ? 1 : -1);
      const pnlPct = openPrice > 0 ? (pnl / (openPrice * quantity / leverage)) * 100 : 0;
      return { value: pnl, pct: pnlPct, isPositive: pnl >= 0 };
    }
    if (orderStatus === 'Closed') {
      const pnlPct = openPrice > 0 ? (finalPnL / (openPrice * quantity / leverage)) * 100 : 0;
      return { value: finalPnL, pct: pnlPct, isPositive: finalPnL >= 0 };
    }
    return { value: 0, pct: 0, isPositive: true };
  };

  const pnlInfo = calculatePnl();
  const isBuy = tradeType.toLowerCase() === 'buy';
  const avgPrice = orderStatus === 'Open' ? openPrice : entryPrice;
  const isClosed = orderStatus === 'Closed';

  const marketStatus = useMemo(() => {
    const type = exchange?.toUpperCase() === 'MCX' ? 'commodity' : 'equity';
    return isMarketOpen(type);
  }, [exchange]);

  const isCarryForward = !isClosed && !marketStatus.isOpen;

  return (
    <div className={`${styles.cardContainer} ${showActions ? styles.cardActive : ''}`} onClick={() => !isClosed && setShowActions(!showActions)}>
      {/* Top Header Row */}
      <div className={styles.cardHeader}>
        <div className={styles.symbolGroup}>
          <span className={`${styles.typeBadge} ${isBuy ? styles.buy : styles.sell}`}>
            {tradeType.toUpperCase()}
          </span>
          <h4 className={styles.symbolName}>{formatSymbol(tradeSymbol)}</h4>
          <span className={styles.exchangeBadge}>{exchange}</span>
        </div>
        <div className={styles.tagsGroup}>
          <span className={styles.positionTypeTag}>{positionType === 'Intraday' ? 'MIS' : 'CNC'}</span>
          {margin > 1 && <span className={styles.mtfTag}>{margin}X</span>}
          {isClosed && <span className={styles.closedTag}>CLOSED</span>}
          {!isClosed && <FiChevronDown className={`${styles.expandIcon} ${showActions ? styles.rotated : ''}`} />}
        </div>
      </div>

      {/* Main Data Row */}
      <div className={styles.mainRow}>
        <div className={styles.dataCol}>
          <label>Qty</label>
          <div className={styles.dataValue}>{quantity}</div>
        </div>
        <div className={styles.dataCol}>
          <label>Avg. Price</label>
          <div className={styles.dataValue}>₹{avgPrice.toFixed(2)}</div>
        </div>
        <div className={styles.dataCol}>
          <label>{isClosed ? 'Exit Price' : 'LTP'}</label>
          <div className={styles.dataValue}>
            ₹{isClosed ? (closePrice?.toFixed(2) || '0.00') : (livePrice?.toFixed(2) || avgPrice.toFixed(2))}
          </div>
        </div>
        <div className={`${styles.dataCol} ${styles.pnlCol}`}>
          <label>PnL</label>
          <div className={`${styles.pnlValue} ${pnlInfo.isPositive ? styles.positive : styles.negative} ${isCarryForward ? styles.pnlDull : ''}`}>
            {pnlInfo.value >= 0 ? '+' : ''}{pnlInfo.value.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Detailed Grid (Visible only when expanded for OPEN positions) */}
      {!isClosed && (
        <div className={`${styles.detailsDrawer} ${showActions ? styles.drawerOpen : ''}`}>
          <div className={styles.divider}></div>
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <span>Market Val</span>
              <span className={styles.boldValue}>₹{(quantity * (livePrice || avgPrice)).toFixed(2)}</span>
            </div>
            <div 
              className={`${styles.detailItem} ${styles.clickableDetail}`}
              onClick={(e) => { e.stopPropagation(); onAction('modifyPosition', _id); }}
            >
              <div className={styles.labelWithEdit}>
                <span>Target</span>
                <FiEdit2 className={styles.editIconMini} />
              </div>
              <span className={styles.targetValue}>{targetPrice ? `₹${targetPrice}` : 'SET'}</span>
            </div>
            <div 
              className={`${styles.detailItem} ${styles.clickableDetail}`}
              onClick={(e) => { e.stopPropagation(); onAction('modifyPosition', _id); }}
            >
              <div className={styles.labelWithEdit}>
                <span>Stoploss</span>
                <FiEdit2 className={styles.editIconMini} />
              </div>
              <span className={styles.slValue}>{stoplossPrice ? `₹${stoplossPrice}` : 'SET'}</span>
            </div>
          </div>

          <div className={styles.actionButtons}>
            {!marketStatus.isOpen ? (
              <div className={styles.marketClosedNote}>
                <FiClock /> Market Closed • Position Carried Forward
              </div>
            ) : (
              <div className={styles.buttonsRow}>
                <button 
                  className={styles.addBtn}
                  onClick={(e) => { e.stopPropagation(); onAction('addQty', _id); }}
                >
                  <FiPlus /> Add
                </button>
                {positionType === 'Intraday' && (
                  <button 
                    className={styles.convertBtn}
                    onClick={(e) => { e.stopPropagation(); onAction('convert', _id); }}
                  >
                    <FiRefreshCw /> Convert
                  </button>
                )}
                <button 
                  className={styles.closeBtn}
                  onClick={(e) => { e.stopPropagation(); onAction('close', _id); }}
                >
                  <FiXCircle /> Exit
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PositionCard;
